import { useCallback, useEffect, useMemo, useState } from 'react'

import { Portal } from '@headlessui/react'

import { useFoodMutationAsync, useFoodSWR } from '~/api-client/food_api'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { DictionaryIcon } from '~/components/icons'

import { DictionaryDialogContentEdit } from './DictionaryDialogContentEdit'
import { DictionaryDialogContentList } from './DictionaryDialogContentList'
import { DictionaryDialogContentNew } from './DictionaryDialogContentNew'

export const DictionaryDialog: React.FC<{
  onSelect: (text: string) => void
}> = (props) => {
  const [confirmDelete, setConfirmDelete] = useState<string>()

  const userDictionary = useFoodSWR('/user/user_dictionary', {})
  const officialDictionary = useFoodSWR('/user/official_dictionary', {})
  const deleteDictionaryTerm = useFoodMutationAsync(
    '/user/delete_user_dictionary_term'
  )

  const dictionary = useMemo(
    () =>
      userDictionary.data?.terms
        .map((term) => ({
          id: term.userDictionaryTermId,
          text: term.text,
          isOfficial: false,
        }))
        ?.concat(
          officialDictionary.data?.terms.map((term) => ({
            id: term.officialDictionaryTermId,
            text: term.text,
            isOfficial: true,
          })) ?? []
        ),
    [userDictionary, officialDictionary]
  )

  const [dialogState, setDialogState] = useState<
    | { type: 'list' }
    | { type: 'new' }
    | { type: 'edit'; term: { id: string; text: string } }
  >()

  const open = useCallback(() => {
    document.body.style.overflow = 'hidden'
    setDialogState({ type: 'list' })
  }, [])

  const close = useCallback(() => {
    document.body.style.overflow = 'initial'
    setDialogState(undefined)
  }, [])

  useEffect(() => {
    return () => {
      close()
    }
  }, [close])

  return (
    <>
      <Modal
        isOpen={confirmDelete != null}
        setIsOpen={(v) => !v && setConfirmDelete(undefined)}
        title="本当に削除しますか？"
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              className="w-full bg-gray"
              variant="functionWeak"
              shrink
              onClick={async () => {
                setConfirmDelete(undefined)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async () => {
                if (confirmDelete) {
                  setConfirmDelete(undefined)
                  await deleteDictionaryTerm({
                    userDictionaryTermId: confirmDelete,
                  })
                  await userDictionary.mutate()
                }
              }}
            >
              削除する
            </Button>
          </div>
        }
      />
      {dialogState != null && (
        <Portal>
          <div className="z-50 inset-0 w-full h-full fixed bg-white select-none overflow-auto">
            {dialogState?.type === 'list' && (
              <DictionaryDialogContentList
                dictionary={dictionary}
                onSelect={(text) => {
                  close()
                  props.onSelect(text)
                }}
                onBack={() => {
                  close()
                }}
                onNew={() => setDialogState({ type: 'new' })}
                onEdit={(term) => setDialogState({ type: 'edit', term })}
                onDelete={(id) => setConfirmDelete(id)}
              />
            )}
            {dialogState?.type === 'new' && (
              <DictionaryDialogContentNew
                onBack={() => setDialogState({ type: 'list' })}
                onCreated={async () => {
                  setDialogState({ type: 'list' })
                  await userDictionary.mutate()
                }}
              />
            )}
            {dialogState?.type === 'edit' && (
              <DictionaryDialogContentEdit
                term={dialogState.term}
                onBack={() => setDialogState({ type: 'list' })}
                onUpdated={async () => {
                  setDialogState({ type: 'list' })
                  await userDictionary.mutate()
                }}
              />
            )}
          </div>
        </Portal>
      )}

      {dialogState == null && (
        <button
          type="button"
          onClick={open}
          // NOTE form label 内で利用されるケースがあるのe font-normal を明示的に指定
          className="gap-1 flex items-center font-normal underline text-sm"
        >
          <DictionaryIcon className="fill-gray" />
          辞書から引用する
        </button>
      )}
    </>
  )
}
